import { useSelector } from "@xstate/react";
import { createContext, use } from "react";
import type { PropsWithChildren } from "react";

import { selectAppearance } from "@/lib/flows/appearance-machine";
import type { AppearanceActorRef } from "@/lib/flows/appearance-machine";
import type { Appearance } from "@/lib/state/appearance";

export type { Appearance } from "@/lib/state/appearance";

const AppearanceActorContext = createContext<AppearanceActorRef | null>(null);

AppearanceActorContext.displayName = "AppearanceActorContext";

export const AppearanceProvider = ({
  actor,
  children,
}: Readonly<PropsWithChildren<{ readonly actor: AppearanceActorRef }>>) => (
  <AppearanceActorContext value={actor}>{children}</AppearanceActorContext>
);

const useAppearanceActor = (): AppearanceActorRef => {
  const actor = use(AppearanceActorContext);

  if (actor === null) {
    throw new Error("useAppearance must be used within AppearanceProvider.");
  }

  return actor;
};

export interface AppearanceState {
  readonly appearance: Appearance;
  readonly updateAppearance: (appearance: Appearance) => void;
}

export const useAppearance = (): AppearanceState => {
  const actor = useAppearanceActor();
  const appearance = useSelector(actor, selectAppearance);

  const updateAppearance = (nextAppearance: Appearance): void => {
    const event = {
      appearance: nextAppearance,
      type: "APPEARANCE_SELECTED",
    } as const;

    if (actor.getSnapshot().can(event)) {
      actor.send(event);
    }
  };

  return { appearance, updateAppearance };
};
